import {
  getChatWorkflowCategories,
  ChatUseCase,
  ChatUseCaseCategory,
} from 'llm/chat-library/use-cases/chatUseCaseCatalog';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Progressive-disclosure view of the shared use case catalog. The chat-box pills, onboarding, and
 * homepage read their prompts through here; the catalog owns the data (and the flat id space the
 * "already tried" signal is keyed on), so a prompt run from any surface counts on all of them.
 */

/** Tenant-resolved option surfaced to UI and onboarding flows. */
export type ProgressiveDisclosureOption = ChatUseCase;

export interface ProgressiveDisclosureCategory {
  id: string;
  label: string;
  icon: BenchlingIconName;
  options: Array<ProgressiveDisclosureOption>;
}

export const INTRO_PROGRESSIVE_DISCLOSURE_MAX_CATEGORIES = 3;

function toProgressiveDisclosureCategory(category: ChatUseCaseCategory): ProgressiveDisclosureCategory {
  return {id: category.id, label: category.label, icon: category.icon, options: category.chatUseCases};
}

/** Progressive-disclosure categories and options visible in the current tenant context. */
export function getProgressiveDisclosureCategories(): Array<ProgressiveDisclosureCategory> {
  return getChatWorkflowCategories().map((category) => toProgressiveDisclosureCategory(category));
}

function shuffled<T>(items: Array<T>, random: () => number): Array<T> {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * The category pills shown in the new-chat intro.
 *
 * There are more categories than pill slots, so a fixed set would leave the rest undiscoverable from
 * the chat box. Categories the user has never run anything from are drawn at random to spread
 * awareness across the whole catalog over repeated visits; categories they have already used fill
 * any remaining slots in catalog order, since reshuffling those buys no discovery and only makes a
 * prompt the user liked harder to find again.
 *
 * Callers must hold the result steady for as long as the pills are on screen (see
 * ProgressiveDisclosure), otherwise re-rendering would deal a new hand mid-interaction.
 */
export function pickIntroProgressiveDisclosureCategories(
  usedChatUseCaseIds: ReadonlySet<string>,
  random: () => number = Math.random
): Array<ProgressiveDisclosureCategory> {
  const categories = getProgressiveDisclosureCategories();
  const isUntried = (category: ProgressiveDisclosureCategory): boolean =>
    category.options.every((option) => !usedChatUseCaseIds.has(option.id));
  return [
    ...shuffled(
      categories.filter((category) => isUntried(category)),
      random
    ),
    ...categories.filter((category) => !isUntried(category)),
  ].slice(0, INTRO_PROGRESSIVE_DISCLOSURE_MAX_CATEGORIES);
}

/** How many categories the user has never run a use case from; the pool the pills draw from. */
export function countUntriedCategories(usedChatUseCaseIds: ReadonlySet<string>): number {
  return getProgressiveDisclosureCategories().filter((category) =>
    category.options.every((option) => !usedChatUseCaseIds.has(option.id))
  ).length;
}
